import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import TopNav from '../components/TopNav';
import BottomTab from '../components/BottomTab';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaInfoCircle } from 'react-icons/fa';
import BookSearchHeader from '../components/BookSearchHeader';
import { Wrapper } from '../styles/CommonStyles';
import { searchBooksByKeyword } from '../api/BookApi';
import { getAccessToken } from '../react-queries/queries';
import { processApiResponse } from '../common/errorMessage';
import { useNoobookContext } from '../context/NoobookContext';
import { BookRegType } from '../common/enums';
import { bookRegType2Path } from '../common/enums';
import BookCoverImage from '../components/BookCoverImage';

const Title = styled.div`
  font-size: 26px;
  font-weight: 700;
  color: #222;
  margin-top: 0;
  margin-bottom: 28px;
  text-align: left;
  padding-left: 24px;
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  background: #fff;
  border-radius: 10px;
  padding: 0 18px;
  margin: 0 24px 32px 24px;
  height: 56px;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
`;

const SearchIcon = styled(FaInfoCircle)`
  font-size: 20px;
  color: #888;
  margin-right: 12px;
`;

const SearchInput = styled.input`
  border: none;
  outline: none;
  font-size: 18px;
  flex: 1;
  background: transparent;
  color: #222;
  &::placeholder {
    color: #bbb;
    opacity: 1;
  }
`;

const ResultCount = styled.div`
  font-size: 16px;
  color: #888;
  font-weight: 500;
  text-align: center;
  padding: 0 0 12px 0;
  span {
    color: #222;
    font-weight: 700;
    margin-left: 6px;
  }
`;

const BookList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const BookItem = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 18px 0 18px 24px;
  border-bottom: 1px solid #eee;
  background: #fff;
  position: relative;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #f8f9fa;
  }
  
  &:active {
    background-color: #e9ecef;
  }
`;

const BookInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const BookTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: #111;
  margin-bottom: 4px;
`;

const BookMeta = styled.div`
  font-size: 15px;
  color: #888;
  margin-bottom: 2px;
  white-space: pre-line;
`;

const BookPub = styled.div`
  font-size: 14px;
  color: #bbb;
`;

const InfoIcon = styled(FaInfoCircle)`
  color: #3cb300;
  font-size: 26px;
  margin-left: 12px;
  margin-top: 8px;
  flex-shrink: 0;
`;

const sampleBooks = [
  {
    thumb: '/book_sample1.jpeg',
    title: '동백꽃 둘다리 외',
    authors: '김유정, 주요섭, 전영택, 하근찬, 황순원',
    publisher: '서연비람',
    pubdate: '2018년 7월 15일',
  },
  {
    thumb: '/book_sample2.jpeg',
    title: '성냥팔이 소녀 외 7편 - 자신의 문해력에 맞춰서 읽는 Solution Book',
    authors: '안데르센',
    publisher: '자유지성사',
    pubdate: '2024년 12월 10일',
  },
  {
    thumb: '/book_sample3.jpeg',
    title: '남방우편기 - 초판본',
    authors: '생텍쥐페리',
    publisher: '더스토리',
    pubdate: '2024년 11월 10일',
  },
  {
    thumb: '/book_sample4.jpeg',
    title: '바스커빌가의 사냥개',
    authors: '아서 코난 도일',
    publisher: '민음사',
    pubdate: '2024년 11월 8일',
  },
  {
    thumb: '/book_sample5.jpeg',
    title: '데미안 - 미니북',
    authors: '헤르만헤세',
    publisher: '더클래식',
    pubdate: '2024년 10월 31일',
  },
  {
    thumb: '/book_sample6.jpeg',
    title: '인간 실격 - 미니북',
    authors: '다자이 오사무',
    publisher: '더클래식',
    pubdate: '2024년 10월 31일',
  },
];

export default function BookSearchResultPage({keyword, onClose}) {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  if(!keyword) {
    keyword = params.get('keyword') || '';
  }
  const bookRegType = params.get('bookRegType') || BookRegType.NUBOOK;
  const [search, setSearch] = useState(keyword);
  const [searchKeyword, setSearchKeyword] = useState(keyword);
  const [books, setBooks] = useState([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(null);
  const navigate = useNavigate();
  const { showBack, setShowBack } = useNoobookContext();
  const headerRef = useRef(null);

  useEffect(() => {
    const prevShowBack = showBack;
    setShowBack(!onClose);
    return () => setShowBack(prevShowBack);
  }, []);

  // 최초 진입 시(특히 iOS 키보드로 스크롤이 밀린 경우) 검색 헤더가 보이도록 스크롤
  useEffect(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    const scrollHeaderIntoView = () => {
      const el = headerRef.current;
      if (!el) return;

      window.scrollTo(0, 0);

      let parent = el.parentElement;
      while (parent && parent !== document.body) {
        const { overflowY } = window.getComputedStyle(parent);
        if (
          (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
          parent.scrollHeight > parent.clientHeight
        ) {
          parent.scrollTop = 0;
        }
        parent = parent.parentElement;
      }

      el.scrollIntoView({ block: 'start', inline: 'nearest' });
    };

    scrollHeaderIntoView();
    const raf = requestAnimationFrame(scrollHeaderIntoView);
    const t1 = setTimeout(scrollHeaderIntoView, 100);
    const t2 = setTimeout(scrollHeaderIntoView, 350);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  useEffect(() => {
    //setSearch(keyword);
    setBooks([]);
    setPage(0);
    setHasMore(true);
    setTotalCount(null);
  }, [keyword, search]);

  useEffect(() => {
    //if (!keyword) return;
  
    loadBooks(0, true);
    // eslint-disable-next-line
  }, [search]);

  const loadBooks = async (nextPage, isInit = false) => {
    if (loading) return;
    setLoading(true);
    try {
      const { accessToken } = await getAccessToken();
      const res = await searchBooksByKeyword(accessToken || '', search/*keyword*/, nextPage, 50);
      
      // API 응답 처리
      const processedResponse = processApiResponse(res);
      
      if (!processedResponse.success) {
        console.error('책 검색 중 오류 발생:', processedResponse.errorMessage);
        setHasMore(false);
        setBooks(prev => Array.isArray(prev) ? prev : []);
        return;
      }
      
      const newBooks = Array.isArray(processedResponse.data) ? processedResponse.data : [];
      setBooks(prev => isInit ? newBooks : [...(Array.isArray(prev) ? prev : []), ...newBooks]);
      setHasMore(newBooks.length === 50);
      setPage(nextPage + 1);
      if (res.page?.total) setTotalCount(res.page?.total);
    } catch (e) {
      console.error('책 검색 중 네트워크 오류 발생:', e);
      setHasMore(false);
      setBooks(prev => Array.isArray(prev) ? prev : []);
    } finally {
      setLoading(false);
    }
  };

  // 무한스크롤: 스크롤이 끝에 닿으면 loadBooks 호출
  useEffect(() => {
    const handleScroll = () => {
      const el = document.scrollingElement || document.documentElement;
      if (!hasMore || loading) return;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 100) {
        loadBooks(page);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [page, hasMore, loading]);

  const handleSearch = () => {
    setSearch(searchKeyword);
    //if (search.trim()) {
      //navigate(`/book-search-result?keyword=${encodeURIComponent(search.trim())}&bookRegType=${bookRegType}`, { replace: true });
    //}
  };

  const handleBookItemClick = (isbn, bookId) => {
    if(onClose) {
      onClose(isbn, bookId);
    } else {
      if (isbn) {
        navigate(`${bookRegType2Path(bookRegType)}?isbn=${encodeURIComponent(isbn)}`, { replace: !!bookRegType });
      } else if (bookId) {
        navigate(`${bookRegType2Path(bookRegType)}?bookId=${encodeURIComponent(bookId)}`, { replace: !!bookRegType });
      }
    }
  };

  return (
    <>
      {/*<TopNav showBack />*/}
      <Wrapper>
        <div ref={headerRef}>
          <BookSearchHeader
            title="어떤 책을 찾으시나요?"
            placeholder="책, 저자, 출판사를 검색해 보세요"
            value={searchKeyword}
            onChange={e => setSearchKeyword(e.target.value)}
            onSearch={handleSearch}
          />
        </div>
        <ResultCount>
          {!loading && <span>검색결과 : {totalCount !== null ? totalCount : books.length}권</span>}
        </ResultCount>
        <BookList>
          {Array.isArray(books) && books.map((book, idx) => (
            <BookItem key={book.isbn13 || book.isbn || idx} onClick={() => handleBookItemClick(book.isbn13 || book.isbn, book.bookId)}>
              <BookCoverImage
                src={book.cover}
                alt={book.title}
                width={60}
                height={84}
                borderRadius={4}
                boxShadow="none"
                marginRight={18}
                fontSize={9}
              />
              <BookInfo>
                <BookTitle>{book.title}</BookTitle>
                <BookMeta>{book.author}</BookMeta>
                <BookPub>
                  {book.publisher}  {book.pubDate}
                </BookPub>
              </BookInfo>
            </BookItem>
          ))}
          {loading && <div style={{textAlign:'center', color:'#888', padding:'16px'}}>불러오는 중...</div>}
          {!loading && (!Array.isArray(books) || books.length === 0) && <div style={{textAlign:'center', color:'#888', padding:'32px'}}>검색 결과가 없습니다.</div>}
        </BookList>
      </Wrapper>
      {/*<BottomTab selected="home" onTabChange={tab => navigate(`/${tab}`)} />*/}
    </>
  );
} 